import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, TextField, CircularProgress, Switch, Avatar,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import InsightsIcon from "@mui/icons-material/Insights";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSnackbar } from "notistack";
import { logo_red } from "../../assets";
import { useSchool } from "../../Hooks/SchoolContext";
import { useContentSetting } from "../../Hooks/ContentSetting";
import InstructorInviteTools from "../../components/InstructorInviteTools";
import InstructorStudentRoster from "../../components/InstructorStudentRoster";
import WorkingHoursDialog from "./Tutors/WorkingHoursDialog";
import {
  create_connect_onboarding_link_api, create_school_connect_onboarding_link_api,
  get_tutoring_profile_api, update_tutoring_profile_api, upload_tutoring_profile_photo_api,
} from "../../DAL/SchoolPortal/Tutors";
import {
  border, textPrimary, textSecondary, textMuted, accent, accentSoft, font, success,
  inputSx, primaryButtonSx, secondaryButtonSx, shadow,
} from "../../layouts/schoolPortal/schoolPortalTheme";
import { regionOf, currencySymbol } from "../../utils/region";

/**
 * Setup, as a slide flow: welcome, the steps this person owes, and a finish.
 *
 * WHY SLIDES AND NOT A CHECKLIST
 * The first version was a card on the dashboard, under the stat tiles — a
 * brand-new instructor read "0 booked / 0 students / nothing needs you" before
 * they read what to do. The second was a checklist on its own page, which
 * fixed the noise but still showed five outstanding jobs at once. One thing at
 * a time, with a welcome at the front, is the same shape as the student
 * onboarding in web-frontend (components/onboarding/OnboardingStyle5.jsx) and
 * asks for one decision per screen.
 *
 * WHICH STEPS, FOR WHOM
 * Decided on the server (web-backend utils/onboarding_steps.js) and read here
 * from `onboarding.applicable`, so the slides are never a second opinion:
 *
 *   group               photo · payouts · rates · students · calendar
 *   school manager      the school's payouts (when the school is paid) · students
 *   school instructor   photo · own payouts (when they are paid) · rates (when
 *                       they set their own) · students · calendar
 *
 * The students step is the school's first student, counted across the whole
 * school — so once anyone at a school has one, every instructor after them
 * finds it already done and is carried straight past it.
 *
 * Transitions are CSS keyframes rather than framer-motion, which is not a
 * dependency of this repo; pages/Instructors/InstructorSignup.jsx animates the
 * same way.
 *
 * Every step is completable here. Nothing else in the portal is reachable
 * until this is done, so a step that sent you elsewhere would be a trap.
 */

const keyframes = `
  @keyframes slideIn {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes popIn {
    0%   { opacity: 0; transform: scale(0.8); }
    60%  { transform: scale(1.06); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes glow {
    0%, 100% { opacity: 0.5; transform: scale(1); }
    50%      { opacity: 0.8; transform: scale(1.06); }
  }
  @keyframes confettiFall {
    0%   { opacity: 1; transform: translate3d(0, -12vh, 0) rotate(0deg); }
    85%  { opacity: 1; }
    100% { opacity: 0; transform: translate3d(var(--drift), 104vh, 0) rotate(var(--spin)); }
  }
`;

const CONFETTI_COLORS = ["#c01e2e", "#f2b544", "#3e8f6b", "#2f6fae", "#e8748a", "#f6f2ec"];
const CONFETTI_COUNT = 80;
// Long enough for the slowest piece to clear the bottom of the screen, after
// which the whole overlay unmounts rather than sitting there animating nothing.
const CONFETTI_MS = 6000;

const makeConfetti = () =>
  Array.from({ length: CONFETTI_COUNT }, (_, id) => ({
    id,
    left: Math.random() * 100,
    delay: Math.random() * 0.9,
    duration: 2.8 + Math.random() * 2.4,
    drift: `${Math.round(-60 + Math.random() * 120)}px`,
    spin: `${Math.round(360 + Math.random() * 720)}deg`,
    color: CONFETTI_COLORS[id % CONFETTI_COLORS.length],
    width: 6 + Math.round(Math.random() * 5),
    height: 9 + Math.round(Math.random() * 8),
    round: Math.random() > 0.75,
  }));

/**
 * A one-off burst across the whole viewport when someone first lands.
 * Fixed and pointer-events:none so it never sits between them and a control,
 * and unmounted on a timer so it does not keep compositing for the rest of the
 * session. Skipped outright for anyone who has asked for reduced motion.
 */
function Confetti() {
  const [pieces] = useState(makeConfetti);
  return (
    <Box aria-hidden sx={{
      position: "fixed", inset: 0, overflow: "hidden",
      pointerEvents: "none", zIndex: 1400,
    }}>
      {pieces.map((p) => (
        <Box
          key={p.id}
          style={{
            "--drift": p.drift,
            "--spin": p.spin,
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: p.width,
            height: p.round ? p.width : p.height,
            borderRadius: p.round ? "50%" : "2px",
            backgroundColor: p.color,
            animation: `confettiFall ${p.duration}s linear ${p.delay}s forwards`,
          }}
        />
      ))}
    </Box>
  );
}

const DAY_LABELS = [
  ["monday", "Mon"], ["tuesday", "Tue"], ["wednesday", "Wed"], ["thursday", "Thu"],
  ["friday", "Fri"], ["saturday", "Sat"], ["sunday", "Sun"],
];

const DEFAULT_HOURS = {
  sunday: { enabled: false, start: "09:00", end: "17:00" },
  monday: { enabled: true, start: "09:00", end: "17:00" },
  tuesday: { enabled: true, start: "09:00", end: "17:00" },
  wednesday: { enabled: true, start: "09:00", end: "17:00" },
  thursday: { enabled: true, start: "09:00", end: "17:00" },
  friday: { enabled: true, start: "09:00", end: "17:00" },
  saturday: { enabled: false, start: "09:00", end: "17:00" },
};

// Every step in its fixed order. Which of them a given person sees comes from
// the server's `applicable`; this is only the order they appear in.
const STEP_KEYS = ["photo", "connect", "rates", "students", "calendar"];

// Long enough that typing "35" is one save rather than two, short enough that
// Continue unlocks while they are still looking at the field.
const RATE_SAVE_DELAY_MS = 800;

/**
 * What each kind of person gets, not what they have to do — the steps speak
 * for themselves as you reach them, and previewing them here just made the
 * welcome read as a to-do list. Three rows at most, for the same reason.
 */
function welcomeItemsFor({ scope, schoolName, connectFor }) {
  if (scope === "school_manager") {
    return [
      {
        icon: <AccountBalanceWalletIcon />,
        title: "Get paid for every session",
        body: connectFor === "school"
          ? "Students book and pay through XAir. Stripe pays the school, and you pay your instructors from there."
          : "Students book and pay through XAir, and Stripe pays each instructor directly.",
      },
      {
        icon: <InsightsIcon />,
        title: "See every student's weak spots",
        body: "Where each student is struggling between lessons, across the whole school, so nobody walks in blind.",
      },
      {
        icon: <GroupAddIcon />,
        title: "Bring your students with you",
        body: "One link for the whole school, or invite students by email. Each is a seat, exactly as today.",
      },
    ];
  }
  if (scope === "school_instructor") {
    return [
      {
        icon: <SchoolIcon />,
        title: "Teach through XAir",
        body: `Students at ${schoolName} book time with you here, at the times you choose.`,
      },
      {
        icon: <InsightsIcon />,
        title: "Know their weak spots before the lesson",
        body: "See what each student is struggling with between lessons, so you walk in already knowing what to cover.",
      },
      {
        icon: <GroupAddIcon />,
        title: "Your school handles the rest",
        body: `${schoolName} brings its students in and looks after billing, so you can get on with teaching.`,
      },
    ];
  }
  return [
    {
      icon: <AccountBalanceWalletIcon />,
      title: "Get paid for teaching",
      body: "Set your own rates. Students book and pay through XAir, and Stripe sends it straight to your bank.",
    },
    {
      icon: <InsightsIcon />,
      title: "Know their weak spots before the lesson",
      body: "See what each student is struggling with between lessons, so you walk in already knowing what to cover.",
    },
    {
      icon: <GroupAddIcon />,
      title: "Keep the students you already teach",
      body: "Invite them to your own group. Each one gets 14 days free, no card needed.",
    },
  ];
}

function Slide({ step, children }) {
  // key on step so the animation replays each time the slide changes
  return (
    <Box key={step} sx={{ animation: "slideIn 0.32s cubic-bezier(0.16, 1, 0.3, 1)" }}>
      {children}
    </Box>
  );
}

function SlideHead({ eyebrow, title, blurb }) {
  return (
    <Box sx={{ mb: 3 }}>
      {eyebrow && (
        <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: accent, mb: 1 }}>
          {eyebrow}
        </Typography>
      )}
      <Typography sx={{ fontFamily: font, fontSize: { xs: "24px", sm: "30px" }, fontWeight: 800, color: textPrimary, letterSpacing: "-0.5px", lineHeight: 1.15 }}>
        {title}
      </Typography>
      {blurb && (
        <Typography sx={{ fontFamily: font, fontSize: "14.5px", color: textSecondary, mt: 1, lineHeight: 1.6 }}>
          {blurb}
        </Typography>
      )}
    </Box>
  );
}

/** A soft-tinted panel — the one recurring container for a step's controls. */
const panelSx = {
  backgroundColor: "#fff",
  border: `1px solid ${border}`,
  borderRadius: "16px",
  boxShadow: shadow,
  p: { xs: 2, sm: 2.5 },
};

export default function InstructorOnboarding() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { onboarding, refreshOnboarding, school } = useSchool();
  const { userInfo } = useContentSetting();

  const [index, setIndex] = useState(0);
  const [profile, setProfile] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [savingRates, setSavingRates] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [rates, setRates] = useState({ min30: "", min60: "" });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [checkingConnect, setCheckingConnect] = useState(false);
  const rateSaveTimer = useRef(null);
  // Cancel a pending autosave if they leave the page mid-type. Nothing is lost
  // that matters: Continue is gated on the save having landed, so an unsaved
  // rate cannot be walked past.
  useEffect(() => () => clearTimeout(rateSaveTimer.current), []);
  // Shown the instant a file is chosen, from a local FileReader, so the face
  // appears before Cloudinary has answered. Replaced by the stored URL on the
  // next refresh.
  const [photoPreview, setPhotoPreview] = useState(null);

  const [confetti, setConfetti] = useState(false);

  // Someone who arrives already finished is sent on; someone who finishes
  // while here stays to see it.
  const wasIncomplete = useRef(false);
  if (onboarding?.applies && !onboarding.complete) wasIncomplete.current = true;

  useEffect(() => { refreshOnboarding(); }, [refreshOnboarding]);

  const loadProfile = async () => {
    try {
      const r = await get_tutoring_profile_api();
      if (r.code === 200) {
        setProfile(r.data);
        const d = r.data?.tutoring?.rates?.default || {};
        setRates({
          min30: d.min30 ? String(d.min30) : "",
          min60: d.min60 ? String(d.min60) : (r.data?.tutoring?.hourlyRate ? String(r.data.tutoring.hourlyRate) : ""),
        });
      }
    } catch (e) { /* the step just stays incomplete */ }
  };

  useEffect(() => { loadProfile(); }, []);

  // ── Who this is, and so which slides they get ──────────────────────────
  const scope = onboarding?.scope || "group";
  const isSchool = scope === "school_manager" || scope === "school_instructor";
  const schoolName = school?.name || (isSchool ? "your school" : "Your group");
  const connectFor = onboarding?.connectFor || "instructor";
  const sym = currencySymbol(regionOf(school));

  // An older server sends no `applicable`; it only ever gated groups, which
  // owe all five, so that is the honest fallback.
  const applicableKey = (onboarding?.applicable?.length ? onboarding.applicable : STEP_KEYS).join(",");
  const flow = useMemo(
    () => ["welcome", ...STEP_KEYS.filter((k) => applicableKey.split(",").includes(k)), "done"],
    [applicableKey]
  );
  const DONE = flow.length - 1;
  const current = flow[Math.min(index, DONE)];
  const stepsInFlow = flow.slice(1, -1);

  const steps = onboarding?.steps || {};
  const done = useMemo(() => Object.fromEntries(
    // A step that does not apply to this person is not outstanding.
    STEP_KEYS.map((k) => [k, applicableKey.split(",").includes(k) ? Boolean(steps[k]) : true])
  ), [applicableKey, steps.photo, steps.connect, steps.rates, steps.students, steps.calendar]); // eslint-disable-line react-hooks/exhaustive-deps

  const doneCount = stepsInFlow.filter((k) => done[k]).length;

  // complete | needs_info | in_review | not_started | unknown, straight from
  // Stripe via the status endpoint. `connectBusy` also covers the very first
  // load, when we have no answer yet and must not claim payouts are missing.
  const connectState = onboarding?.connectState || "not_started";
  const connectBusy = checkingConnect || !onboarding;

  const studentsJoined = onboarding?.studentsJoined || 0;
  const studentsPending = onboarding?.studentsPending || 0;

  // Someone coming back mid-flow — most often from Stripe, which leaves the
  // site entirely — should land on what they still have to do, not read the
  // welcome again. Runs once, on the first status we get.
  const positioned = useRef(false);
  const [readyToCelebrate, setReadyToCelebrate] = useState(false);
  useEffect(() => {
    if (positioned.current || !onboarding?.applies) return;
    positioned.current = true;
    if (onboarding.complete) { setIndex(DONE); setReadyToCelebrate(true); return; }
    const firstOutstanding = flow.findIndex((k, i) => i > 0 && i < DONE && !done[k]);
    if (doneCount > 0 && firstOutstanding > 0) setIndex(firstOutstanding);
    setReadyToCelebrate(true);
  }, [onboarding, done, doneCount, flow, DONE]);

  // The welcome burst. It waits for the positioning above rather than firing on
  // mount: until the first status lands the welcome slide is only a placeholder,
  // and a refresh part-way through the flow resolves to an outstanding step,
  // where confetti would be celebrating nothing. Once per visit, and never for
  // anyone who has asked their OS for reduced motion.
  const celebrated = useRef(false);
  useEffect(() => {
    if (celebrated.current || !readyToCelebrate || current !== "welcome") return;
    celebrated.current = true;
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    } catch (e) { /* no matchMedia to ask — assume motion is welcome */ }
    setConfetti(true);
  }, [readyToCelebrate, current]);

  useEffect(() => {
    if (!confetti) return undefined;
    const timer = setTimeout(() => setConfetti(false), CONFETTI_MS);
    return () => clearTimeout(timer);
  }, [confetti]);

  const workingHours = profile?.tutoring?.workingHours || DEFAULT_HOURS;
  const photoUrl = photoPreview || profile?.tutoring?.photoUrl || onboarding?.photoUrl || null;
  const firstName = userInfo?.first_name || "";

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    // Let the same file be picked again after a failed upload — without this
    // the input holds the old value and onChange never fires a second time.
    event.target.value = "";
    if (!file) return;

    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      enqueueSnackbar("Your photo needs to be a JPEG, PNG or WebP", { variant: "error" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar("Your photo needs to be under 5MB", { variant: "error" });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      const res = await upload_tutoring_profile_photo_api(formData);
      if (res.code === 200) {
        enqueueSnackbar("Looking good", { variant: "success" });
        await loadProfile();
        refreshOnboarding();
      } else {
        setPhotoPreview(null);
        enqueueSnackbar(res.message || "Could not upload your photo", { variant: "error" });
      }
    } catch (e) {
      setPhotoPreview(null);
      enqueueSnackbar("Could not upload your photo", { variant: "error" });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Whose Stripe account this step is about comes from the server: the
  // school's when the school is the one paid, otherwise the person's own.
  const handleConnect = async () => {
    setConnecting(true);
    const res = connectFor === "school"
      ? await create_school_connect_onboarding_link_api("/school/onboarding")
      : await create_connect_onboarding_link_api("/school/onboarding");
    if (res.code === 200 && res.data?.url) {
      window.location.href = res.data.url;
      return;
    }
    enqueueSnackbar(res.message || "Could not start Stripe onboarding", { variant: "error" });
    setConnecting(false);
  };

  // The server re-checks against Stripe whenever it thinks payouts are
  // unfinished, so a plain refresh is all this needs.
  const handleRecheckConnect = async () => {
    setCheckingConnect(true);
    await refreshOnboarding();
    setCheckingConnect(false);
  };

  /**
   * Rates autosave a beat after typing stops.
   *
   * Deliberately quiet about the empty case: someone clearing a box to retype
   * it is mid-thought, not making a mistake, so there is no error and no write
   * — the step simply stays outstanding, and Continue stays disabled, which is
   * the honest signal. Only a real failed write speaks up.
   *
   * `loadProfile()` is NOT called after a successful save. It would overwrite
   * both inputs from the server, and if the second field was being typed into
   * while the first saved, that keystroke would vanish under the response.
   */
  const saveRates = async (value) => {
    const min30 = Number(value.min30);
    const min60 = Number(value.min60);
    if (!(min30 > 0) && !(min60 > 0)) return;

    setSavingRates(true);
    const payload = { default: {} };
    if (min30 > 0) payload.default.min30 = min30;
    if (min60 > 0) payload.default.min60 = min60;
    const res = await update_tutoring_profile_api({
      rates: payload,
      hourlyRate: min60 > 0 ? min60 : undefined,
    });
    setSavingRates(false);
    if (res.code === 200) {
      refreshOnboarding();
    } else {
      enqueueSnackbar(res.message || "Could not save your rates", { variant: "error" });
    }
  };

  const handleRateChange = (key) => (event) => {
    const next = { ...rates, [key]: event.target.value };
    setRates(next);
    clearTimeout(rateSaveTimer.current);
    rateSaveTimer.current = setTimeout(() => saveRates(next), RATE_SAVE_DELAY_MS);
  };

  const handleSaveHours = async (hours) => {
    setSavingHours(true);
    const res = await update_tutoring_profile_api({ workingHours: hours });
    setSavingHours(false);
    if (res.code === 200) {
      setHoursOpen(false);
      enqueueSnackbar("Working hours saved", { variant: "success" });
      await loadProfile();
    } else {
      enqueueSnackbar(res.message || "Could not save your hours", { variant: "error" });
    }
  };

  const handleToggleBookable = async (checked) => {
    const res = await update_tutoring_profile_api({ isBookable: checked });
    if (res.code === 200) {
      await loadProfile();
      refreshOnboarding();
      // The finale: turning this on is the last thing, so carry them to it
      // rather than making them press Continue on their own celebration.
      if (checked) setTimeout(() => setIndex(DONE), 450);
    } else {
      enqueueSnackbar(res.message || "Could not update your calendar", { variant: "error" });
    }
  };

  const smallButtonSx = { ...secondaryButtonSx, minHeight: 34, padding: "6px 14px", fontSize: "12.5px" };

  // Continue is gated on the step actually being finished — this flow is the
  // gate, so letting someone click past a step would defeat it.
  const canContinue = current === "welcome" || (STEP_KEYS.includes(current) ? done[current] : true);
  const continueLabel = current === "welcome"
    ? (scope === "school_manager" ? "Let's set up your school"
      : scope === "school_instructor" ? "Let's set up your profile"
        : "Let's set up your group")
    : (index === DONE - 1 ? "Finish" : "Continue");
  const eyebrow = (key) => `Step ${flow.indexOf(key)} of ${stepsInFlow.length}`;

  if (onboarding && (!onboarding.applies || (onboarding.complete && !wasIncomplete.current))) {
    return <Navigate to="/school/dashboard" replace />;
  }

  const welcomeBlurb = scope === "school_manager"
    ? `${schoolName} is on XAir. A couple of things to set up and your instructors can start teaching.`
    : scope === "school_instructor"
      ? `You're part of ${schoolName} on XAir. Set up your teaching profile and you're ready for bookings.`
      : "Your group is ready. Here's what it does for you — we'll get you set up in under ten minutes.";

  const studentsBlurb = !isSchool
    ? "Send your link to the students you already teach, or invite them by email. Each one gets 14 days free with no card. You can move on as soon as the first one is in."
    : scope === "school_manager"
      ? `Share ${schoolName}'s link or invite students by email. You can move on as soon as the school has its first student.`
      : `${schoolName} doesn't have a student on XAir yet. Share the school's link or invite one — the moment anyone at the school has a student, this is done for every instructor.`;

  const doneTitle = scope === "school_manager" ? "Your school is set up" : "You're live";
  const doneBlurb = scope === "school_manager"
    ? `${schoolName} can take bookings as soon as each instructor has finished their own profile.`
    : scope === "school_instructor"
      ? `${schoolName}'s students can book you now, and you'll see where each of them is weak before the next lesson.`
      : `${school?.name || "Your group"} is open for bookings. Your students can book you at your rates, and you'll see where each of them is weak before the next lesson.`;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}>
      <style>{keyframes}</style>
      {confetti && <Confetti />}

      {/* The welcome slide shows the logo large, as its own hero — two would
          just stack on top of each other. */}
      {current !== "welcome" && (
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
          <img src={logo_red} alt="XAir" style={{ height: 44, width: "auto" }} />
        </Box>
      )}

      {/* Progress dots. Welcome and finish are part of the journey but not
          things to do, so only the steps this person owes get a dot. */}
      {current !== "welcome" && current !== "done" && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mb: 4 }}>
          {stepsInFlow.map((key) => {
            const isCurrent = key === current;
            return (
              <Box key={key} sx={{
                height: 6, borderRadius: 999,
                width: isCurrent ? 28 : 6,
                backgroundColor: done[key] ? success : (isCurrent ? accent : "rgba(26,26,26,0.15)"),
                transition: "width 0.3s ease, background-color 0.3s ease",
              }} />
            );
          })}
        </Box>
      )}

      <Box sx={{ flex: 1, width: "100%", maxWidth: 620, mx: "auto" }}>
        <Slide step={current}>
          {current === "welcome" && (
            <Box sx={{ textAlign: "center" }}>
              {/* The logo carries the welcome, so the page-level one is
                  suppressed on this slide — see the header above. */}
              <Box
                component="img"
                src={logo_red}
                alt="XAir"
                sx={{
                  height: 88, width: "auto", display: "block", mx: "auto", mb: 3,
                  animation: "popIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              />
              <SlideHead
                title={firstName ? `Welcome to XAir, ${firstName}` : "Welcome to XAir"}
                blurb={welcomeBlurb}
              />
              <Box sx={{ ...panelSx, textAlign: "left" }}>
                {welcomeItemsFor({ scope, schoolName, connectFor }).map((item, i) => (
                  <Box key={item.title} sx={{
                    display: "flex", gap: 2, alignItems: "flex-start",
                    py: 1.75, borderTop: i === 0 ? "none" : `1px solid ${border}`,
                  }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: "11px", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      backgroundColor: accentSoft, color: accent,
                      "& svg": { fontSize: 20 },
                    }}>
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: font, fontSize: "14.5px", fontWeight: 700, color: textPrimary }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary, lineHeight: 1.55, mt: 0.25 }}>
                        {item.body}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {current === "photo" && (
            <>
              <SlideHead
                eyebrow={eyebrow("photo")}
                title="Put a face to your name"
                blurb="Students choose an instructor from a card with a photo on it. A clear headshot does more than anything else on this page. JPEG, PNG or WebP, under 5MB."
              />
              <Box sx={{ ...panelSx, display: "flex", alignItems: "center", gap: 2.5 }}>
                <Avatar
                  src={photoUrl || undefined}
                  sx={{
                    width: 84, height: 84, bgcolor: accentSoft, color: accent, border: `1px solid ${border}`,
                    animation: photoUrl ? "popIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)" : "none",
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Box>
                  <Button component="label" disabled={uploadingPhoto} sx={primaryButtonSx}>
                    {uploadingPhoto ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : (photoUrl ? "Choose a different photo" : "Upload a photo")}
                    <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handlePhotoChange} />
                  </Button>
                  {done.photo && (
                    <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: success, fontWeight: 600, mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 15 }} /> Saved
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}

          {current === "connect" && (
            <>
              <SlideHead
                eyebrow={eyebrow("connect")}
                title={connectFor === "school" ? "Set up your school's payouts" : "Get paid"}
                blurb={connectFor === "school"
                  ? `When a student pays for a session, Stripe sends it to ${schoolName}'s account, and you pay your instructors from there. You'll need the school's bank details and a director's photo ID. It opens in Stripe and brings you straight back here.`
                  : "Stripe pays you directly when a student books a session — it never sits with us. You'll need your bank details and a photo ID. It opens in Stripe and brings you straight back here."}
              />
              {/* One control, centred, with no panel around it — a card whose
                  only contents are a single button is a border for its own
                  sake. What it says comes from Stripe's own view of the
                  account, not from a flag we set on the way past. */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 1 }}>
                {connectBusy && (
                  <Typography sx={{ fontFamily: font, fontSize: "14px", color: textSecondary, display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={15} sx={{ color: accent }} /> Checking with Stripe
                  </Typography>
                )}

                {!connectBusy && done.connect && (
                  <Typography sx={{ fontFamily: font, fontSize: "14.5px", color: success, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.75 }}>
                    <CheckCircleIcon sx={{ fontSize: 19 }} /> {connectFor === "school" ? "School payouts connected" : "Payouts connected"}
                  </Typography>
                )}

                {/* Everything submitted, nothing outstanding, Stripe still
                    checking. Sending them back into the flow here would be a
                    loop with no fields left to fill in, so offer a re-check
                    instead. */}
                {!connectBusy && !done.connect && connectState === "in_review" && (
                  <>
                    <Typography sx={{ fontFamily: font, fontSize: "14.5px", color: textPrimary, fontWeight: 700, textAlign: "center" }}>
                      Stripe is reviewing the details
                    </Typography>
                    <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary, textAlign: "center", maxWidth: 380 }}>
                      Nothing more to do. This usually takes a few minutes, occasionally longer.
                    </Typography>
                    <Button onClick={handleRecheckConnect} sx={{ ...secondaryButtonSx, minWidth: 180 }}>
                      Check again
                    </Button>
                  </>
                )}

                {!connectBusy && !done.connect && connectState !== "in_review" && (
                  <>
                    {connectState === "needs_info" && (
                      <Typography sx={{ fontFamily: font, fontSize: "13.5px", color: textSecondary, textAlign: "center", maxWidth: 380 }}>
                        Stripe still needs {onboarding?.connectOutstanding === 1 ? "one more detail" : "a few more details"} before it can pay out.
                      </Typography>
                    )}
                    <Button
                      onClick={handleConnect}
                      disabled={connecting}
                      sx={{ ...primaryButtonSx, minWidth: 240, minHeight: 48, fontSize: "15px" }}
                    >
                      {connecting
                        ? <CircularProgress size={18} sx={{ color: "#fff" }} />
                        : (onboarding?.connectAccountExists
                          ? "Continue Stripe setup"
                          : (connectFor === "school" ? "Connect the school's payouts" : "Connect payouts"))}
                    </Button>
                  </>
                )}
              </Box>
            </>
          )}

          {current === "rates" && (
            <>
              <SlideHead
                eyebrow={eyebrow("rates")}
                title="Name your price"
                blurb="What you charge per session. Fill in one or both — you can price individual subjects differently once you're up and running."
              />
              <Box sx={panelSx}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.75 }}>
                  {/* The school's own currency. A US instructor typing a price
                      beside a pound sign has been told the wrong unit on the
                      one field where the unit is the whole point. */}
                  <TextField
                    label="30 minutes" size="small" type="number" value={rates.min30}
                    onChange={handleRateChange("min30")}
                    InputProps={{ startAdornment: <Typography sx={{ fontFamily: font, fontSize: "13px", color: textMuted, mr: 0.5 }}>{sym}</Typography> }}
                    sx={inputSx}
                  />
                  <TextField
                    label="60 minutes" size="small" type="number" value={rates.min60}
                    onChange={handleRateChange("min60")}
                    InputProps={{ startAdornment: <Typography sx={{ fontFamily: font, fontSize: "13px", color: textMuted, mr: 0.5 }}>{sym}</Typography> }}
                    sx={inputSx}
                  />
                </Box>
                {/* Autosaved. The line below is the only feedback, so it has to
                    hold a slot whether or not there is anything to say —
                    otherwise the panel jumps every time it appears. */}
                <Box sx={{ minHeight: 22, display: "flex", alignItems: "center", mt: 1.5 }}>
                  {savingRates && (
                    <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, display: "flex", alignItems: "center", gap: 0.75 }}>
                      <CircularProgress size={13} sx={{ color: accent }} /> Saving
                    </Typography>
                  )}
                  {!savingRates && done.rates && (
                    <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: success, fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 15 }} /> Saved automatically
                    </Typography>
                  )}
                  {!savingRates && !done.rates && (
                    <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textMuted }}>
                      Saves on its own as you type.
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}

          {current === "students" && (
            <>
              <SlideHead
                eyebrow={eyebrow("students")}
                title="Bring your students in"
                blurb={studentsBlurb}
              />
              {/* Add first, then see who is in. The action this step is for
                  leads; the roster below is the result of it, and grows
                  downwards rather than pushing the controls off the screen as
                  people are added. */}
              <Box sx={{ ...panelSx, mb: 2 }}>
                <InstructorInviteTools onStudentAdded={refreshOnboarding} />
              </Box>

              <Box sx={panelSx}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.25 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: textSecondary }}>
                    {isSchool ? `Students at ${schoolName}` : "Your students"}
                  </Typography>
                  {done.students ? (
                    <Typography sx={{ fontFamily: font, fontSize: "12px", color: success, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 15 }} />
                      {studentsJoined === 1 ? "1 joined" : `${studentsJoined} joined`}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, display: "flex", alignItems: "center", gap: 0.75 }}>
                      <CircularProgress size={12} sx={{ color: accent }} />
                      {studentsPending > 0 ? "Waiting for someone to join" : "Nobody yet"}
                    </Typography>
                  )}
                </Box>

                <InstructorStudentRoster
                  active={current === "students"}
                  refreshToken={`${studentsJoined}-${studentsPending}`}
                  onJoinedCountChange={refreshOnboarding}
                />

                {!done.students && (
                  <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mt: 1.5, lineHeight: 1.5 }}>
                    {studentsPending > 0
                      ? "Inviting isn't joining — they still have to create their account. This updates on its own the moment one of them does."
                      : "This updates on its own as soon as someone joins."}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {current === "calendar" && (
            <>
              <SlideHead
                eyebrow={eyebrow("calendar")}
                title="When are you free?"
                blurb="Students can only book inside these hours. Change them whenever you like — this is a default, not a commitment."
              />
              <Box sx={panelSx}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.75 }}>
                  {DAY_LABELS.map(([key, label]) => {
                    const day = workingHours[key] || {};
                    return (
                      <Box key={key} sx={{
                        px: 1.25, py: 0.75, borderRadius: "9px", minWidth: 66, textAlign: "center",
                        border: `1px solid ${day.enabled ? "rgba(192,30,46,0.25)" : border}`,
                        backgroundColor: day.enabled ? accentSoft : "transparent",
                      }}>
                        <Typography sx={{ fontFamily: font, fontSize: "11.5px", fontWeight: 700, color: day.enabled ? accent : textMuted }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontFamily: font, fontSize: "10px", color: day.enabled ? textSecondary : textMuted }}>
                          {day.enabled ? `${day.start}-${day.end}` : "Off"}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Button onClick={() => setHoursOpen(true)} sx={smallButtonSx}>Edit hours</Button>

                <Box sx={{
                  mt: 2.5, p: 1.75, borderRadius: "12px",
                  border: `1px solid ${done.calendar ? `${success}55` : "rgba(192,30,46,0.25)"}`,
                  backgroundColor: done.calendar ? `${success}0f` : accentSoft,
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
                }}>
                  <Box>
                    <Typography sx={{ fontFamily: font, fontSize: "13.5px", fontWeight: 800, color: textPrimary }}>
                      Take bookings
                    </Typography>
                    <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, lineHeight: 1.45 }}>
                      Flip this and you're live. It's the last thing.
                    </Typography>
                  </Box>
                  <Switch
                    checked={Boolean(profile?.tutoring?.isBookable)}
                    onChange={(e) => handleToggleBookable(e.target.checked)}
                    sx={{ "& .Mui-checked": { color: accent }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: accent } }}
                  />
                </Box>
              </Box>
            </>
          )}

          {current === "done" && (
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ position: "relative", display: "inline-flex", mb: 2.5 }}>
                <Box sx={{
                  position: "absolute", inset: -10, borderRadius: "50%",
                  background: `radial-gradient(circle, ${success}44, transparent 70%)`,
                  animation: "glow 3s ease-in-out infinite", pointerEvents: "none",
                }} />
                <Avatar
                  src={photoUrl || undefined}
                  sx={{ width: 84, height: 84, border: `2px solid ${success}`, animation: "popIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)" }}
                >
                  <CheckCircleIcon sx={{ fontSize: 38, color: success }} />
                </Avatar>
              </Box>
              <SlideHead title={doneTitle} blurb={doneBlurb} />
              <Button
                onClick={() => navigate("/school/dashboard")}
                endIcon={<ArrowForwardIcon />}
                sx={{ ...primaryButtonSx, width: "100%" }}
              >
                Go to your dashboard
              </Button>
            </Box>
          )}
        </Slide>

        {/* Footer. The finish slide carries its own CTA. */}
        {current !== "done" && (
          <Box sx={{ display: "flex", gap: 1.5, mt: 4 }}>
            {current !== "welcome" && (
              <Button onClick={() => setIndex((i) => Math.max(0, i - 1))} sx={{ ...secondaryButtonSx, flex: 1 }}>
                Back
              </Button>
            )}
            <Button
              onClick={() => setIndex((i) => Math.min(DONE, i + 1))}
              disabled={!canContinue}
              endIcon={<ArrowForwardIcon />}
              sx={{ ...primaryButtonSx, flex: current !== "welcome" ? 2 : 1, "&.Mui-disabled": { opacity: 0.45, color: "#fff" } }}
            >
              {continueLabel}
            </Button>
          </Box>
        )}

        {current !== "welcome" && current !== "done" && !canContinue && (
          <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted, textAlign: "center", mt: 1.5 }}>
            Finish this step to carry on.
          </Typography>
        )}
      </Box>

      <WorkingHoursDialog
        open={hoursOpen}
        initialValue={workingHours}
        saving={savingHours}
        onClose={() => setHoursOpen(false)}
        onSave={handleSaveHours}
      />
    </Box>
  );
}
